import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Bot, Info, ShieldAlert } from "lucide-react";

/**
 * Shared, calm compliance notices. Wording is intentionally short and
 * proportionate — shown at the point of use, not buried in the Terms.
 */

/** Best-effort local emergency number from the device time zone. Never assumed. */
function guessEmergencyNumber(): string | null {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    if (tz === "Europe/London" || tz === "Europe/Belfast") return "999";
    if (tz === "Europe/Dublin") return "112 or 999";
    if (tz.startsWith("Australia/")) return "000";
    if (tz === "Pacific/Auckland") return "111";
    if (tz === "Asia/Kolkata" || tz === "Asia/Calcutta") return "112";
    if (tz.startsWith("Europe/")) return "112";
    if (
      tz.startsWith("America/") &&
      /New_York|Chicago|Denver|Los_Angeles|Phoenix|Anchorage|Detroit|Toronto|Vancouver|Edmonton|Winnipeg|Halifax|Indiana|Kentucky|Boise/.test(tz)
    )
      return "911";
    if (tz === "Pacific/Honolulu") return "911";
  } catch {
    /* ignore */
  }
  return null;
}

/** "Contact your local emergency services" line, with a local example only when we can infer one. */
export function EmergencyLine({ className = "" }: { className?: string }) {
  const [num, setNum] = useState<string | null>(null);
  useEffect(() => setNum(guessEmergencyNumber()), []);
  return (
    <span className={className}>
      If you are in immediate danger, contact your local emergency services
      {num ? ` (${num} where you appear to be)` : ""}.{" "}
      <Link to="/crisis" className="font-medium underline underline-offset-2">
        Find crisis support in your country
      </Link>
      .
    </span>
  );
}

export function NotADiagnosisNote({ className = "" }: { className?: string }) {
  return (
    <p
      role="note"
      className={`flex gap-2 rounded-xl border border-border bg-muted/40 px-3 py-2.5 text-xs leading-relaxed text-muted-foreground ${className}`}
    >
      <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
      <span>
        <strong className="text-foreground">Not a diagnosis.</strong> This result is for education and
        self-reflection only. It isn't a medical or psychological assessment. If anything here
        resonates or worries you, a qualified professional can help you explore it.
      </span>
    </p>
  );
}

type PracticeKind = "somatic" | "breathing" | "inner-child";

const PRACTICE_COPY: Record<PracticeKind, string> = {
  somatic:
    "Go gently. You can stop at any time if you feel distressed, dizzy, in pain or unsafe. If you have an injury, are pregnant or have a health condition, check with a medical professional first.",
  breathing:
    "Breathe gently and skip any hold that doesn't feel right. If you feel lightheaded or dizzy, return to your normal breathing. If you have a heart, lung or blood-pressure condition, epilepsy, or are pregnant, speak to a doctor before breath-holding practices.",
  "inner-child":
    "If a memory or feeling becomes too much, pause, ground yourself and come back later — or not at all. You never have to revisit painful memories. Consider exploring this with a qualified professional.",
};

export function PracticeSafetyNote({ kind, className = "" }: { kind: PracticeKind; className?: string }) {
  return (
    <aside
      role="note"
      aria-label="Before you begin"
      className={`flex gap-2.5 rounded-xl border border-gold/30 bg-gold-light/15 px-3.5 py-3 text-xs leading-relaxed text-foreground/80 ${className}`}
    >
      <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-[var(--gold-deep)]" aria-hidden />
      <span>
        {PRACTICE_COPY[kind]} This is a wellbeing practice, not medical treatment.
      </span>
    </aside>
  );
}

/** Persistent AI identification + limits for chat surfaces. */
export function AiChatNotice({ className = "" }: { className?: string }) {
  return (
    <p
      role="note"
      className={`flex gap-2 text-[11px] leading-relaxed text-muted-foreground ${className}`}
    >
      <Bot className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
      <span>
        You're chatting with <strong className="text-foreground">AI</strong>, not a person. It can make
        mistakes and is not therapy, medical advice or a crisis service, and it isn't watched by a
        person in real time. <EmergencyLine />{" "}
        <Link to="/ai-disclaimer" className="font-medium underline underline-offset-2">
          AI Disclaimer
        </Link>
      </span>
    </p>
  );
}
